import { LockOutlined, NumberOutlined, UserOutlined } from '@ant-design/icons';
import { Capacitor } from '@capacitor/core';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from 'components/ui/form';
import { Input } from 'components/ui/input';
import { PasswordInput } from 'components/ui/password-input';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { routes } from 'services/RouteService';
import AuthContainer, { AuthContainerProps } from '../containers/AuthContainer';

export type LoginRegisterFormProps = AuthContainerProps & {
  askForLicenseCode?: boolean;
  displayCodeFull?: boolean;
  displayCodeExpired?: boolean;
  buttonTitle: string;
  buttonLoading: boolean;
  onButtonClick: (params: {
    email: string;
    password: string;
    first_name?: string;
    last_name?: string;
    re_password?: string;
    license_code?: string;
  }) => void;
  passwordServerError?: string;
  initialValues?: {
    email?: string;
    re_email?: string;
    password?: string;
    re_password?: string;
    first_name?: string;
    last_name?: string;
    tc_and_privacy_policy_consent?: boolean;
    personal_information_access_consent?: boolean;
  };
};

type FormValues = {
  email: string;
  re_email: string;
  password: string;
  re_password: string;
  first_name: string;
  last_name: string;
  license_code: string;
  tc_and_privacy_policy_consent: boolean;
  personal_information_access_consent: boolean;
};

const EMAIL_REGEX = /^[A-Za-z0-9+_.-]+@(.+)$/;

// E-mails and codes are shown lowercase; the placeholder keeps its capital.
const textInputClassName = 'pl-9 text-base lowercase placeholder:normal-case';
const secretInputClassName = 'pl-9 text-base';
// z-10: PasswordInput's own positioned wrapper would otherwise paint over the icon
const prefixIconClassName = 'pointer-events-none absolute top-1/2 left-3 z-10 -translate-y-1/2 text-muted-foreground';

export default function LoginRegisterForm(props: LoginRegisterFormProps) {
  const {
    title,
    description,
    buttonTitle,
    mode,
    onButtonClick,
    buttonLoading,
    bottomLinkTo,
    askForLicenseCode,
    displayCodeFull,
    displayCodeExpired,
    passwordServerError,
    initialValues,
    onBackButtonClick,
  } = props;

  // Hooks
  const { t } = useTranslation();
  const form = useForm<FormValues>({
    // Ant Design validated on every change; keep that behaviour.
    mode: 'onChange',
    defaultValues: {
      email: '',
      re_email: '',
      password: '',
      re_password: '',
      first_name: '',
      last_name: '',
      license_code: '',
      tc_and_privacy_policy_consent: false,
      personal_information_access_consent: false,
      ...initialValues,
    },
  });

  useEffect(() => {
    if (passwordServerError) {
      form.setError('password', {
        type: 'server',
        message: t(`loginRegister.fields.password.errors.${passwordServerError}`),
      });
    }
  }, [form, passwordServerError, t]);

  const onFinish = (values: FormValues) => {
    onButtonClick({
      email: values.email,
      password: values.password,
      ...(mode === 'register' && {
        first_name: values.first_name,
        last_name: values.last_name,
        license_code: values.license_code || undefined,
        re_password: values.re_password,
        tc_and_privacy_policy_consent: values.tc_and_privacy_policy_consent,
        personal_information_access_consent: values.personal_information_access_consent,
        email_notifications_consent: !(Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android'),
      }),
    });
  };

  // The regex also rejects an empty value, as the old `required` + validator pair did.
  const validateEmail = (value: string) => EMAIL_REGEX.test(value) || t('loginRegister.fields.email.errors.notEmail');

  const validatePassword = (value: string) => {
    if (!value) {
      return true;
    }

    const hasMinLength = value.length >= 12;
    const hasLowercase = /[a-z]/.test(value);
    const hasUppercase = /[A-Z]/.test(value);
    const hasDigit = /\d/.test(value);

    if (hasMinLength && hasLowercase && hasUppercase && hasDigit) {
      return true;
    }
    return t('loginRegister.fields.password.errors.requirements');
  };

  return (
    <AuthContainer
      title={title}
      description={description}
      mode={mode}
      bottomLinkTo={bottomLinkTo}
      displayBackButton
      onBackButtonClick={onBackButtonClick}
    >
      <Form {...form}>
        <form id="login-form" onSubmit={form.handleSubmit(onFinish)} className="flex flex-col gap-5">
          {/* the address, confirmed */}
          <div className="flex flex-col gap-2">
            <FormField
              control={form.control}
              name="email"
              rules={{ validate: validateEmail, deps: ['re_email'] }}
              render={({ field }) => (
                <FormItem>
                  <div className="relative">
                    <UserOutlined className={prefixIconClassName} />
                    <FormControl>
                      <Input
                        {...field}
                        autoCapitalize="none"
                        placeholder={t('loginRegister.fields.email.placeholder')}
                        className={textInputClassName}
                        autoComplete="username"
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {mode === 'register' && (
              <FormField
                control={form.control}
                name="re_email"
                rules={{
                  required: t('loginRegister.fields.emailConfirmation.errors.required'),
                  validate: value =>
                    !value ||
                    form.getValues('email') === value ||
                    t('loginRegister.fields.emailConfirmation.errors.emailsNotMatch'),
                }}
                render={({ field }) => (
                  <FormItem>
                    <div className="relative">
                      <UserOutlined className={prefixIconClassName} />
                      <FormControl>
                        <Input
                          {...field}
                          autoCapitalize="none"
                          placeholder={t('loginRegister.fields.emailConfirmation.placeholder')}
                          className={textInputClassName}
                          autoComplete="username"
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>
          {/* the password, confirmed */}
          <div className="flex flex-col gap-2">
            <FormField
              control={form.control}
              name="password"
              rules={{
                required: t('loginRegister.fields.password.errors.required'),
                validate: value => mode !== 'register' || validatePassword(value),
                deps: ['re_password'],
              }}
              render={({ field }) => (
                <FormItem>
                  <div className="relative">
                    <LockOutlined className={prefixIconClassName} />
                    <FormControl>
                      <PasswordInput
                        {...field}
                        autoCapitalize="none"
                        placeholder={t('loginRegister.fields.password.placeholder')}
                        className={secretInputClassName}
                        autoComplete="current-password"
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            {mode === 'login' && (
              <div className="text-right">
                <Link to={routes.passwordResetSendEmail.route} className="underline">
                  {t('loginRegister.actions.passwordReset')}
                </Link>
              </div>
            )}

            {mode === 'register' && (
              <FormField
                control={form.control}
                name="re_password"
                rules={{
                  required: t('loginRegister.fields.passwordConfirmation.errors.required'),
                  validate: value =>
                    !value ||
                    form.getValues('password') === value ||
                    t('loginRegister.fields.passwordConfirmation.errors.passwordsNotMatch'),
                }}
                render={({ field }) => (
                  <FormItem>
                    <div className="relative">
                      <LockOutlined className={prefixIconClassName} />
                      <FormControl>
                        <PasswordInput
                          {...field}
                          autoCapitalize="none"
                          placeholder={t('loginRegister.fields.passwordConfirmation.placeholder')}
                          className={secretInputClassName}
                          autoComplete="current-password"
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>
          {/* the name */}
          <div className="flex flex-col gap-2">
            {mode === 'register' && (
              <FormField
                control={form.control}
                name="first_name"
                rules={{ required: t('loginRegister.fields.firstName.errors.required') }}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input {...field} placeholder={t('loginRegister.fields.firstName.placeholder')} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            {mode === 'register' && (
              <FormField
                control={form.control}
                name="last_name"
                rules={{ required: t('loginRegister.fields.lastName.errors.required') }}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input {...field} placeholder={t('loginRegister.fields.lastName.placeholder')} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>
          {mode === 'register' && askForLicenseCode && (
            <div>
              <p className="mb-1 text-subtle-foreground">{t('loginRegister.fields.licenseCode.description')}</p>
              <FormField
                control={form.control}
                name="license_code"
                render={({ field }) => (
                  <FormItem>
                    <div className="relative">
                      <NumberOutlined className={prefixIconClassName} />
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t('loginRegister.fields.licenseCode.placeholder')}
                          className={textInputClassName}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {displayCodeFull && (
                <p className="mt-1 mb-0 text-destructive-text">{t('loginRegister.fields.licenseCode.full')}</p>
              )}
              {displayCodeExpired && (
                <p className="mt-1 mb-0 text-destructive-text">{t('loginRegister.fields.licenseCode.expired')}</p>
              )}
            </div>
          )}
          {mode === 'register' && (
            <FormField
              control={form.control}
              name="tc_and_privacy_policy_consent"
              rules={{
                validate: value => value || t('loginRegister.fields.tc_and_privacy_policy_consent.errors.required'),
              }}
              render={({ field }) => (
                <FormItem>
                  {/*
                   * GYN-238: top-aligned so a wrapped label does not vertically
                   * centre the box; `shrink-0` so the label cannot squeeze it.
                   * `FormControl` gives the checkbox the id `FormLabel` points
                   * to, which is what keeps `get_by_label("J'ai lu et
                   * j'accepte…")` working.
                   */}
                  <div className="flex items-start gap-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={checked => field.onChange(checked === true)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                        className="mt-0.5 shrink-0"
                      />
                    </FormControl>
                    <FormLabel className="inline">
                      <Link to={t('legal.termsAndPrivacy')} target="_blank" className="underline">
                        {t('loginRegister.fields.tc_and_privacy_policy_consent.title')}
                      </Link>{' '}
                      *
                    </FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          {mode === 'register' && (
            <FormField
              control={form.control}
              name="personal_information_access_consent"
              rules={{
                validate: value =>
                  value || t('loginRegister.fields.personal_information_access_consent.errors.required'),
              }}
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-start gap-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={checked => field.onChange(checked === true)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                        className="mt-0.5 shrink-0"
                      />
                    </FormControl>
                    <FormLabel className="inline">
                      {t('loginRegister.fields.personal_information_access_consent.title')} *
                    </FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <Button type="submit" size="xl" className="w-full" disabled={buttonLoading} loading={buttonLoading}>
            {buttonTitle}
          </Button>
        </form>
      </Form>
    </AuthContainer>
  );
}
